/**
 * 修改密码对话框。
 *
 * 设计选择：**改完后保持登录**。
 * 服务端契约里 `POST /api/auth/password` 只改密码，不吊销其它会话；
 * 强行把用户踢回登录页反而会让人以为"改密码把账号搞坏了"。
 * 因此这里成功后就地提示，不改登录态。
 */
import { useState } from 'react'
import type { FormEvent } from 'react'
import { AuthError, changePassword } from '../api/auth'

interface Props {
  onClose: () => void
}

function messageOf(err: unknown): string {
  if (!(err instanceof AuthError)) return err instanceof Error ? err.message : String(err)
  if (err.status === 0) return '无法连接服务端'
  if (err.status === 401) return '当前密码不正确'
  if (err.status === 400) return '新密码不符合要求（至少 8 位）'
  if (err.status === 403) return '会话已失效，请重新登录后再改密码'
  return `修改失败（HTTP ${err.status}）`
}

export default function PasswordDialog({ onClose }: Props) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)
  const [busy, setBusy] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (busy) return
    setError('')
    if (current === '') {
      setError('请输入当前密码')
      return
    }
    if (next.length < 8) {
      setError('新密码至少 8 位')
      return
    }
    if (next !== confirm) {
      setError('两次输入的新密码不一致')
      return
    }
    setBusy(true)
    try {
      await changePassword(current, next)
      setDone(true)
    } catch (err) {
      setError(messageOf(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="dialog-mask" role="presentation" onMouseDown={() => (busy ? undefined : onClose())}>
      <form
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-label="修改密码"
        onMouseDown={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <h3 className="dialog__title">修改密码</h3>

        {done ? (
          <>
            <p className="dialog__msg">密码已修改。当前会话保持登录状态。</p>
            <div className="dialog__actions">
              <button type="button" className="dialog__btn dialog__btn--primary" onClick={onClose}>
                好
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="dialog__msg">修改后请用新密码登录其它设备。</p>

            <input
              className="dialog__input"
              type="password"
              placeholder="当前密码"
              aria-label="当前密码"
              autoComplete="current-password"
              autoFocus
              disabled={busy}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
            <input
              className="dialog__input"
              type="password"
              placeholder="新密码（至少 8 位）"
              aria-label="新密码"
              autoComplete="new-password"
              disabled={busy}
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
            <input
              className="dialog__input"
              type="password"
              placeholder="确认新密码"
              aria-label="确认新密码"
              autoComplete="new-password"
              disabled={busy}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />

            {error !== '' && <p className="dialog__msg dialog__msg--err">{error}</p>}

            <div className="dialog__actions">
              <button type="button" className="dialog__btn" disabled={busy} onClick={onClose}>
                取消
              </button>
              <button type="submit" className="dialog__btn dialog__btn--primary" disabled={busy}>
                {busy ? '提交中…' : '确定修改'}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  )
}
